/**
 * ============================================================================
 * [P1-03] ProgressBar Component
 *
 * 진행률 바 컴포넌트
 *
 * 기능:
 * - 글자 수 기준 진행률 시각화
 * - 상태에 따른 색상 변경 (normal, warning, danger, complete)
 *
 * Safety:
 * - 순수 프레젠테이셔널 컴포넌트 (상태 없음)
 * - 진행률 0~100% 범위 제한
 * ============================================================================
 */

import styles from './ProgressBar.module.css'

// ============================================================================
// 타입 정의
// ============================================================================
interface ProgressBarProps {
  current: number
  limit: number
}

// ============================================================================
// 상태 계산 유틸리티
// ============================================================================
function getProgressStatus(percentage: number, isOver: boolean): string {
  if (isOver) return 'danger'
  if (percentage >= 100) return 'complete'
  if (percentage >= 90) return 'warning'
  return 'normal'
}

// ============================================================================
// [P1-03] ProgressBar 컴포넌트
// ============================================================================
export function ProgressBar({ current, limit }: ProgressBarProps) {
  // 진행률 계산 (최소 0%, 최대 100%)
  const percentage = Math.min(Math.max((current / limit) * 100, 0), 100)
  const isOver = current > limit
  const status = getProgressStatus(percentage, isOver)

  return (
    <div className={styles.progressBarContainer}>
      <div
        className={`${styles.progressBar} ${styles[status]}`}
        style={{ width: `${percentage}%` }}
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label={`${current}자 / ${limit}자 작성됨`}
      />
    </div>
  )
}

// ============================================================================
// 기본 export
// ============================================================================
export default ProgressBar
